import { ActionIndex } from '@/flightpath-components/models/ActionIndex';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { SimplePoint } from '@/flightpath-components/models/SimplePoint';
import { SinglePathMission } from '@/flightpath-components/models/SinglePathMission';
import {
  booleanPointInPolygon,
  booleanPointOnLine,
  getCoords,
  lineIntersect,
  lineString,
  Position,
  polygon,
  flattenEach,
  booleanEqual,
  point,
  Feature,
  Polygon,
  Properties,
  booleanCrosses
} from '@turf/turf';

export type IntersectResult = { ActionIndex: number; Enter: boolean; Added: boolean };

const PATH_ACTION_ENTER = 'enter';
const PATH_ACTION_EXIT = 'exit';
/**
 * Helper Class to Retrieve Points of a path that intercept
 * with a work area.
 */
export class IntersectActionHelper {
  /**
   * Validates a polygon to ensure start and endpoint is the same
   * @param area polygon to validate before processing with Turf
   * @returns GeoJson Coords of the polygon
   */
  public static validateWorkArea(area: SimplePoint[][]): Position[] {
    const workArea = area[0].map((o) => [o.Long, o.Lat, o.Alt]);
    if (workArea[0] != workArea[workArea.length - 1]) workArea.push(workArea[0]);

    return workArea;
  }

  /**
   * Finds All Mission Path boundry intercept points and inserts into the path
   * @param mission single path mission to add PathActions.
   * @returns Modified SinglePathMission with path intercepts added to PathActions
   */
  public static modifyMissionIntersects(mission: SinglePathMission, calculateCrossing = false): SinglePathMission {
    if (mission.WorkAreas == null || mission.WorkAreas.length == 0 || mission.WorkAreas[0].length == 0) return mission;
    const path = mission.Path.map((o) => [o.Long, o.Lat, o.Alt]);

    const workArea = IntersectActionHelper.validateWorkArea(mission.WorkAreas);

    const intersectionPoints = this.getIntersectionPoints(path, workArea);

    let intersectionResult = IntersectActionHelper.splicePathIntersects(path, workArea, intersectionPoints, calculateCrossing);

    if (intersectionResult[0].length == 0 && intersectionPoints.length > 0)
      intersectionResult = IntersectActionHelper.splicePathIntersectsV2(path, workArea, intersectionPoints, calculateCrossing);

    if (intersectionResult[0].length == 0) return mission;

    mission.Path = intersectionResult[0].map((pos) => {
      return { Lat: pos[1], Long: pos[0], Alt: pos[2] } as SimplePoint;
    });

    mission.PathActions = intersectionResult[1].map((o) => {
      return { Action: o.Enter ? PATH_ACTION_ENTER : PATH_ACTION_EXIT, Index: o.ActionIndex };
    });
    return mission;
  }

  /**
   * Finds All Path boundry intercept points and inserts into the path
   * @param simplePath path to add polygon intercepts
   * @returns Tuple containing modified path, intercept results (Path Actions)
   */
  public static modifyPathIntersects(simplePath: SimplePath, calculateCrossing = false): [SimplePath, IntersectResult[]] {
    if (simplePath.WorkAreas == []) return [simplePath, []];
    const path = simplePath.Path.map((o) => [o.Long, o.Lat, o.Alt]);
    const workArea = IntersectActionHelper.validateWorkArea(simplePath.WorkAreas);

    const intersectionPoints = this.getIntersectionPoints(path, workArea);

    let intersectionResult = IntersectActionHelper.splicePathIntersects(path, workArea, intersectionPoints, calculateCrossing);

    if (intersectionResult[0].length == 0 && intersectionPoints.length > 0)
      intersectionResult = IntersectActionHelper.splicePathIntersectsV2(path, workArea, intersectionPoints, calculateCrossing);

    if (intersectionResult[0].length == 0) return [simplePath, []];

    simplePath.Path = intersectionResult[0].map((pos) => {
      return { Lat: pos[1], Long: pos[0], Alt: pos[2] } as SimplePoint;
    });
    return [simplePath, intersectionResult[1]];
  }

  /**
   * Checks if a set of coords is inside a polygon
   * @param point point to check if inside a polygon
   * @param polygon polygon to compare against
   * @returns boolean if point is in the polygon
   */
  private static isEnteringArea(point: Position, polygon: Feature<Polygon, Properties>, options: any = null) {
    if (point == undefined) return false;
    const inside = booleanPointInPolygon(point, polygon, options);
    return !inside;
  }

  /**
   * Modifies a set of coords with intercept points with a polygon
   * @param path original path to check for polygon intercepts
   * @param workArea polygon to check for intercepts
   * @param intersectionPoints list of intercept points
   * @returns Tuple of modified path, PathActions
   */
  public static splicePathIntersects(
    path: Position[],
    workArea: Position[],
    intersectionPoints: Position[],
    calculateCrossing = false
  ): [Position[], IntersectResult[]] {
    const result: IntersectResult[] = [];
    let insertionIndex = 0;

    const polygonBoundry = polygon([[...workArea]]);
    const turfOptions = { ignoreBoundary: false };
    const firstElementInside = booleanPointInPolygon(path[0], polygonBoundry, turfOptions);
    let inside = firstElementInside;
    for (let index = 0; index < path.length; index++) {
      const element = path[index];
      const intersection = insertionIndex < intersectionPoints.length ? intersectionPoints[insertionIndex] : undefined;
      const pointOnIntersection = intersection != undefined ? booleanEqual(point([intersection[0], intersection[1]]), point([element[0], element[1]])) : false;

      let currentPointInside = booleanPointInPolygon(element, polygonBoundry, turfOptions);

      const lineSegment = index == 0 ? undefined : lineString([path[index - 1], path[index]]);
      if (pointOnIntersection) {
        let isEntering = IntersectActionHelper.isEnteringArea(path[index - 1], polygonBoundry, turfOptions);
        if (index == 0) {
          isEntering = firstElementInside;
        }
        result.push({ ActionIndex: index, Enter: isEntering, Added: false });
        currentPointInside = isEntering;
        insertionIndex++;
      } else if (index === 0 && inside === true && !pointOnIntersection) {
        result.push({ ActionIndex: index, Enter: true, Added: false });
        index++;
      } else if (currentPointInside !== inside && !pointOnIntersection) {
        const prevIndex = index - 1;
        const foundIndex = result.findIndex((o) => o.ActionIndex == prevIndex);
        if (foundIndex == -1) {
          const isEntering = IntersectActionHelper.isEnteringArea(path[prevIndex], polygonBoundry, turfOptions);
          const el = intersectionPoints[insertionIndex];
          if (el == undefined) return [[], []];
          if (el[2] === undefined) el[2] = path[index][2]; // get previous altitude
          path.splice(index, 0, el);
          result.push({ ActionIndex: index, Enter: isEntering, Added: true });
          index++;
          insertionIndex++;
        }
      } else if (calculateCrossing && lineSegment != undefined && booleanCrosses(lineSegment, polygonBoundry)) {
        //line crosses but never changes state
        //return if prev point on a line
        const foundIndex = result.findIndex((o) => o.ActionIndex == index - 1);
        if (foundIndex == -1) {
          const subIntersectionPoints = lineIntersect(lineSegment, polygonBoundry);
          const coords: Position[] = [];
          flattenEach(subIntersectionPoints, function (currentFeature) {
            coords.push(getCoords(currentFeature.geometry));
          });

          coords.forEach(() => {
            const isEntering = IntersectActionHelper.isEnteringArea(path[index - 1], polygonBoundry, turfOptions);
            const spliceIndex = index;
            const el = intersectionPoints[insertionIndex];
            if (el[2] === undefined) el[2] = path[index][2]; // get previous altitude
            path.splice(spliceIndex, 0, el);
            result.push({ ActionIndex: spliceIndex, Enter: isEntering, Added: true });
            insertionIndex++;
            index++;
          });
        }
      }

      inside = currentPointInside;
    }
    return [path, result];
  }

  public static splicePathIntersectsV2(
    path: Position[],
    workArea: Position[],
    intersectionPoints: Position[],
    calculateCrossing = false
  ): [Position[], IntersectResult[]] {
    const result: IntersectResult[] = [];

    const polygonBoundry = polygon([[...workArea]]);
    const turfOptions = { ignoreBoundary: false };
    const firstElementInside = booleanPointInPolygon(path[0], polygonBoundry, turfOptions);
    let inside = firstElementInside;
    for (let index = 0; index < path.length; index++) {
      const element = path[index];
      const pt = point([element[0], element[1]]);

      const pointOnIntersection = intersectionPoints.some((intersection) => {
        const itrPoint = point([intersection[0], intersection[1]]);
        return booleanEqual(itrPoint, pt);
      });

      let currentPointInside = booleanPointInPolygon(element, polygonBoundry, turfOptions);

      const lineSegment = index == 0 ? undefined : lineString([path[index - 1], path[index]]);
      if (pointOnIntersection) {
        let isEntering = IntersectActionHelper.isEnteringArea(path[index - 1], polygonBoundry, turfOptions);
        if (index == 0) {
          isEntering = firstElementInside;
        }
        result.push({ ActionIndex: index, Enter: isEntering, Added: false });
        currentPointInside = isEntering;
      } else if (index === 0 && inside === true && !pointOnIntersection) {
        result.push({ ActionIndex: index, Enter: true, Added: false });
        index++;
      } else if (currentPointInside !== inside && !pointOnIntersection) {
        const prevIndex = index - 1;
        const foundIndex = result.findIndex((o) => o.ActionIndex == prevIndex);
        if (foundIndex == -1 && lineSegment != undefined && booleanCrosses(lineSegment, polygonBoundry)) {
          const isEntering = IntersectActionHelper.isEnteringArea(path[prevIndex], polygonBoundry, turfOptions);

          const subIntersectionPoints = lineIntersect(lineSegment, polygonBoundry);
          const coords: Position[] = [];
          flattenEach(subIntersectionPoints, function (currentFeature) {
            coords.push(getCoords(currentFeature.geometry));
          });

          coords.forEach((coord) => {
            const spliceIndex = index;
            path.splice(spliceIndex, 0, coord);
            result.push({ ActionIndex: spliceIndex, Enter: isEntering, Added: true });
            index++;
          });
        }
      } else if (calculateCrossing && lineSegment != undefined && booleanCrosses(lineSegment, polygonBoundry)) {
        //line crosses but never changes state
        //return if prev point on a line
        const foundIndex = result.findIndex((o) => o.ActionIndex == index - 1);
        if (foundIndex == -1) {
          const subIntersectionPoints = lineIntersect(lineSegment, polygonBoundry);
          const coords: Position[] = [];
          flattenEach(subIntersectionPoints, function (currentFeature) {
            coords.push(getCoords(currentFeature.geometry));
          });

          coords.forEach((coord) => {
            const isEntering = IntersectActionHelper.isEnteringArea(path[index - 1], polygonBoundry, turfOptions);
            const spliceIndex = index;
            path.splice(spliceIndex, 0, coord);
            result.push({ ActionIndex: spliceIndex, Enter: isEntering, Added: true });
            index++;
          });
        }
      }

      inside = currentPointInside;
    }
    return [path, result];
  }
  /**
   * Checks for polyline, polygon intercept points
   * @param path path to check for intercepts
   * @param workArea polygon to check for intercepts
   * @returns points of intercept along a path
   */
  public static getIntersectionPoints(path: Position[], workArea: Position[]): Position[] {
    const pathString = lineString(path);
    const polygonBoundry = polygon([[...workArea]]);
    const coords: Position[] = [];
    const intersectionFeatureCollectionPoints = lineIntersect(pathString, polygonBoundry);
    flattenEach(intersectionFeatureCollectionPoints, function (currentFeature) {
      coords.push(getCoords(currentFeature.geometry));
    });

    return coords;
  }
}